import {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
  type RefObject,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MarkerType,
  useReactFlow,
  getNodesBounds,
  type Node,
  type Edge,
} from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { Printer } from "lucide-react";
import { Button } from "../ui/button";
import PersonalDiagramNode from "./PersonalDiagramNode";
import PersonalDiagramEdge from "./PersonalDiagramEdge";
import {
  normalizePersonalDiagramContent,
  composeEdgeLabelDisplay,
  type PersonalDiagramContent,
  type PersonalDiagramStoredNode,
} from "./personalDiagramTypes";
import {
  formatFieldValue,
  findFieldOptions,
} from "../../lib/formatFieldValue";
import "@xyflow/react/dist/style.css";

const nodeTypes = { personalDiagramNode: PersonalDiagramNode };
const edgeTypes = { personalDiagramEdge: PersonalDiagramEdge };

const FIT_VIEW_OPTS = {
  padding: 0.04,
  minZoom: 0.35,
  maxZoom: 2.5,
} as const;

const AUTO_HEIGHT_MIN = 280;
const AUTO_HEIGHT_MAX = 520;
const AUTO_HEIGHT_PAD_PX = 20;

/** Spread author positions so readonly nodes aren’t cramped, keeping relative layout. */
function spreadNodePositions(
  nodes: PersonalDiagramStoredNode[],
  minCenterDistance = 280,
): PersonalDiagramStoredNode[] {
  if (nodes.length < 2) return nodes;

  let minDist = Infinity;
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].position.x - nodes[j].position.x;
      const dy = nodes[i].position.y - nodes[j].position.y;
      const d = Math.hypot(dx, dy);
      if (d > 0 && d < minDist) minDist = d;
    }
  }

  if (!Number.isFinite(minDist) || minDist >= minCenterDistance) {
    return nodes;
  }

  const scale = minCenterDistance / minDist;
  const cx =
    nodes.reduce((s, n) => s + n.position.x, 0) / nodes.length;
  const cy =
    nodes.reduce((s, n) => s + n.position.y, 0) / nodes.length;

  return nodes.map((n) => ({
    ...n,
    position: {
      x: cx + (n.position.x - cx) * scale,
      y: cy + (n.position.y - cy) * scale,
    },
  }));
}

/**
 * Fits the graph, then reports a canvas height that hugs the content so we
 * don’t leave tall empty bands above/below.
 */
function FitViewController({
  nodeCount,
  containerRef,
  autoSize,
  onAutoHeight,
}: {
  nodeCount: number;
  containerRef: RefObject<HTMLDivElement | null>;
  autoSize: boolean;
  onAutoHeight: (h: number) => void;
}) {
  const { fitView, getNodes, getViewport } = useReactFlow();
  const sizingRef = useRef(false);
  const lastHeightRef = useRef(0);

  const runFit = useCallback(() => {
    requestAnimationFrame(() => {
      fitView({ ...FIT_VIEW_OPTS, duration: 0 });

      if (!autoSize || sizingRef.current) return;

      requestAnimationFrame(() => {
        const ns = getNodes();
        if (!ns.length) return;
        const bounds = getNodesBounds(ns);
        const { zoom } = getViewport();
        const next = Math.round(
          Math.min(
            AUTO_HEIGHT_MAX,
            Math.max(
              AUTO_HEIGHT_MIN,
              bounds.height * zoom + AUTO_HEIGHT_PAD_PX * 2,
            ),
          ),
        );
        if (Math.abs(next - lastHeightRef.current) < 8) return;
        lastHeightRef.current = next;
        sizingRef.current = true;
        onAutoHeight(next);
        requestAnimationFrame(() => {
          fitView({ ...FIT_VIEW_OPTS, duration: 0 });
          sizingRef.current = false;
        });
      });
    });
  }, [fitView, getNodes, getViewport, autoSize, onAutoHeight]);

  useEffect(() => {
    runFit();
  }, [runFit, nodeCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      const onResize = () => runFit();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (sizingRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => runFit());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [runFit, containerRef]);

  return null;
}

type PersonalDiagramProps = {
  content: PersonalDiagramContent;
  userData?: Record<string, unknown>;
  diagramData?: Record<string, any> | null;
  /** When true, hide the print button (e.g. tiny Item preview). */
  hideToolbar?: boolean;
  /**
   * Fixed canvas height. When omitted, height hugs the fitted diagram
   * (less empty space above/below).
   */
  height?: number | string;
  className?: string;
};

/**
 * Runtime / review personal_diagram — read-only ReactFlow filled from userData.
 */
export default function PersonalDiagram({
  content,
  userData = {},
  diagramData = null,
  hideToolbar = false,
  height,
  className = "",
}: PersonalDiagramProps) {
  const { t } = useTranslation();
  const printRootRef = useRef<HTMLDivElement>(null);
  const emptyPlaceholder = t("personal_diagram_empty_value");
  const autoSize = height == null;
  const [autoHeight, setAutoHeight] = useState(360);

  const { nodes: storedNodes, edges: storedEdges } = useMemo(
    () => normalizePersonalDiagramContent(content),
    [content],
  );

  const { nodes, edges } = useMemo(() => {
    const spaced = spreadNodePositions(storedNodes);

    const rfNodes: Node[] = spaced.map((n, index) => {
      const valueLines = (n.fieldKeys || []).map((key) => {
        const raw = userData[key];
        const options = findFieldOptions(diagramData, key);
        const formatted = formatFieldValue(raw, options);
        return formatted === "" ? emptyPlaceholder : formatted;
      });
      return {
        id: n.id,
        type: "personalDiagramNode",
        position: n.position,
        data: {
          authorText: n.authorText || "",
          valueLines: (n.fieldKeys || []).length > 0 ? valueLines : [],
          emptyPlaceholder,
          variant: "readonly" as const,
          colorIndex: index,
        },
        draggable: false,
        selectable: false,
        connectable: false,
      };
    });

    const rfEdges: Edge[] = storedEdges.map((e) => {
      const fieldKeys = e.fieldKeys || [];
      const boundParts = fieldKeys.map((key) => {
        const raw = userData[key];
        const options = findFieldOptions(diagramData, key);
        const formatted = formatFieldValue(raw, options);
        return formatted === "" ? emptyPlaceholder : formatted;
      });
      const label = composeEdgeLabelDisplay(e.label, boundParts);
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        type: "personalDiagramEdge",
        label: label || undefined,
        data: { variant: "readonly" as const },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#64748b",
          width: 20,
          height: 20,
        },
      };
    });

    return { nodes: rfNodes, edges: rfEdges };
  }, [storedNodes, storedEdges, userData, diagramData, emptyPlaceholder]);

  const handlePrint = useCallback(() => {
    const root = printRootRef.current;
    if (!root) {
      window.print();
      return;
    }

    const mount = document.createElement("div");
    mount.className = "personal-diagram-print-mount";
    mount.setAttribute("aria-hidden", "true");

    const clone = root.cloneNode(true) as HTMLElement;
    clone.classList.add("personal-diagram-print-root");
    clone.style.width = `${root.offsetWidth}px`;
    clone.style.height = `${root.offsetHeight}px`;
    clone
      .querySelectorAll(".react-flow__controls, .print\\:hidden")
      .forEach((el) => el.remove());

    mount.appendChild(clone);
    document.body.appendChild(mount);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      mount.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.setTimeout(cleanup, 60_000);

    requestAnimationFrame(() => {
      window.print();
    });
  }, []);

  if (nodes.length === 0) {
    return (
      <div
        className={`rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-6 text-center text-sm text-muted-foreground ${className}`}
      >
        {t("personal_diagram_empty_canvas")}
      </div>
    );
  }

  const canvasHeight = height ?? autoHeight;

  return (
    <div className={`personal-diagram-wrapper mt-3 ${className}`}>
      {!hideToolbar && (
        <div className="flex justify-end mb-2 print:hidden">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4" />
            {t("personal_diagram_print")}
          </Button>
        </div>
      )}
      <div
        ref={printRootRef}
        className="relative rounded-xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 overflow-hidden personal-diagram-canvas shadow-sm"
        style={{ height: canvasHeight }}
      >
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={FIT_VIEW_OPTS}
            onInit={(instance) => {
              requestAnimationFrame(() => {
                instance.fitView({ ...FIT_VIEW_OPTS, duration: 0 });
              });
            }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag
            panOnScroll={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            preventScrolling
            proOptions={{ hideAttribution: true }}
            minZoom={0.3}
            maxZoom={2.5}
            className="bg-transparent"
          >
            <FitViewController
              nodeCount={nodes.length}
              containerRef={printRootRef}
              autoSize={autoSize}
              onAutoHeight={setAutoHeight}
            />
            <Controls
              showInteractive={false}
              showFitView
              showZoom
              position="bottom-right"
              className="!shadow-md !rounded-lg !border !border-slate-200 !overflow-hidden print:hidden"
            />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
}
